import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { API } from "../config/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Users, Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";


const POZITII = [
  { value: "avocat_colaborator", label: "Avocat Colaborator" },
  { value: "avocat_stagiar", label: "Avocat Stagiar" },
  { value: "paralegal", label: "Paralegal" },
  { value: "colaborator_extern", label: "Colaborator Extern" },
];

const initialFormState = {
  name: "",
  email: "",
  password: "",
  phone: "",
  position: "",
  role: "team",
};

export default function TeamMembersPage() {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeader());
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch team members");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: "",
        phone: user.phone || "",
        position: user.position || "",
        role: user.role,
      });
    } else {
      setSelectedUser(null);
      setFormData(initialFormState);
    }
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData(initialFormState);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || (!selectedUser && !formData.password)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        delete updateData.role;
        
        await axios.put(`${API}/users/${selectedUser.id}`, updateData, getAuthHeader());
        toast.success("Team member updated");
      } else {
        await axios.post(`${API}/users`, formData, getAuthHeader());
        toast.success("Team member created");
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`, getAuthHeader());
      toast.success("Team member deleted");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete team member");
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă membrii echipei...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-members-page">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Membrii Echipei</h1>
          <p className="font-body text-muted-foreground mt-1">
            Gestionează echipa cabinetului de avocatură
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-team-member-btn">
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Membru
        </Button>
      </div>
      </div>{/* End Sticky */}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Users className="w-5 h-5" />
            Toți Membrii Echipei ({users.filter((u) => u.role === "team").length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.filter((u) => u.role === "team").length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">Nu există membri în echipă.</p>
              <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
                Adaugă Primul Membru
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-body font-semibold">Nume</TableHead>
                    <TableHead className="font-body font-semibold">Email</TableHead>
                    <TableHead className="font-body font-semibold">Parolă</TableHead>
                    <TableHead className="font-body font-semibold">Poziție</TableHead>
                    <TableHead className="font-body font-semibold">Telefon</TableHead>
                    <TableHead className="font-body font-semibold">Rol</TableHead>
                    <TableHead className="font-body font-semibold text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    .filter((u) => u.role === "team")
                    .map((user) => (
                      <TableRow key={user.id} className="table-row-hover" data-testid={`team-row-${user.id}`}>
                        <TableCell className="font-body font-medium">{user.name}</TableCell>
                        <TableCell className="font-mono text-sm">{user.email}</TableCell>
                        <TableCell className="font-mono text-sm text-muted-foreground">
                          {user.plain_password || "••••••••"}
                        </TableCell>
                        <TableCell className="font-body">{user.position || "-"}</TableCell>
                        <TableCell className="font-mono text-sm">{user.phone || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-body capitalize">
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => openDeleteDialog(user)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="team-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedUser ? "Editează Membru" : "Adaugă Membru Nou"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-body">Nume *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Popescu Ion"
                data-testid="input-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ion@cabinet.ro"
                data-testid="input-email"
              />
              {selectedUser && (
                <p className="text-xs text-muted-foreground">
                  Schimbarea email-ului va actualiza datele de autentificare
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-body">
                Parolă {selectedUser ? "(lăsați gol pentru a păstra actuala)" : "*"}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="pr-10"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="font-body">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="0722 123 456"
                  data-testid="input-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position" className="font-body">Poziție *</Label>
                <Select
                  value={formData.position}
                  onValueChange={(value) => setFormData({ ...formData, position: value })}
                >
                  <SelectTrigger data-testid="select-position">
                    <SelectValue placeholder="Selectează poziția" />
                  </SelectTrigger>
                  <SelectContent>
                    {POZITII.map((poz) => (
                      <SelectItem key={poz.value} value={poz.value}>
                        {poz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!selectedUser && (
              <div className="space-y-2">
                <Label htmlFor="role" className="font-body">Rol</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="select-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="team">Membru Echipă</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Anulează
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-team-member">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se salvează...
                  </>
                ) : selectedUser ? (
                  "Actualizează"
                ) : (
                  "Creează"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Team Member</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
